import { useState } from "react";
import { useStats, type StatsWindow, type Stats as StatsT } from "../lib/api.js";

function StatCard({
  label,
  value,
  sub,
  accent = "purple",
}: {
  label: string;
  value: string | number;
  sub?: string;
  accent?: "purple" | "orange" | "neutral";
}) {
  const accentBar: Record<"purple" | "orange" | "neutral", string> = {
    purple: "bg-gradient-to-b from-brand-purple/80 to-brand-purpleDark/40",
    orange: "bg-gradient-to-b from-brand-orange/80 to-brand-orangeDark/40",
    neutral: "bg-zinc-700/40",
  };
  const accentText: Record<"purple" | "orange" | "neutral", string> = {
    purple: "text-purple-100",
    orange: "text-orange-100",
    neutral: "text-zinc-50",
  };
  return (
    <div className="card overflow-hidden p-5">
      <div className={`absolute left-0 top-0 h-full w-0.5 ${accentBar[accent]}`} />
      <div className="stat-label">{label}</div>
      <div className={`mt-2 stat-num ${accentText[accent]}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
    </div>
  );
}

function fmtDuration(min: number): string {
  if (!Number.isFinite(min) || min <= 0) return "—";
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h < 24) return `${h}h ${m}m`;
  const d = Math.floor(h / 24);
  const hr = h % 24;
  return `${d}d ${hr}h`;
}

function EventsCard({
  data,
  isLoading,
  window,
  onWindowChange,
}: {
  data?: StatsT["events"];
  isLoading: boolean;
  window: StatsWindow;
  onWindowChange: (w: StatsWindow) => void;
}) {
  const fmt = (n: number) => (isLoading ? "—" : n.toLocaleString());
  const live = (data?.last5min ?? 0) > 0;
  return (
    <div className="card overflow-hidden p-5">
      <div className="absolute left-0 top-0 h-full w-0.5 bg-gradient-to-b from-brand-purple/80 to-brand-purpleDark/40" />
      <div className="flex items-start justify-between gap-2">
        <div className="stat-label">Events</div>
        <div className="flex rounded border border-zinc-700/50 text-[10px] uppercase tracking-wide">
          {(["24h", "all"] as const).map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => onWindowChange(w)}
              className={`px-1.5 py-0.5 ${
                w === window
                  ? "bg-brand-purple/30 text-purple-100"
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {w === "all" ? "all-time" : w}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-zinc-500">Rentals</div>
          <div className="text-lg font-semibold text-purple-100">{fmt(data?.rentals ?? 0)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-zinc-500">Returns</div>
          <div className="text-lg font-semibold text-purple-100">{fmt(data?.returns ?? 0)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-zinc-500">Problems</div>
          <div
            className={`text-lg font-semibold ${
              (data?.problems ?? 0) > 0 ? "text-orange-300" : "text-purple-100"
            }`}
          >
            {fmt(data?.problems ?? 0)}
          </div>
        </div>
      </div>
      <div className="mt-1 flex items-center gap-1.5 text-xs text-zinc-500">
        <span
          className={`inline-block h-1.5 w-1.5 rounded-full ${
            live ? "bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.6)]" : "bg-zinc-600"
          }`}
          aria-label={live ? "live" : "quiet"}
        />
        {fmt(data?.last5min ?? 0)} rental {(data?.last5min ?? 0) === 1 ? "action" : "actions"} in last 5 min
      </div>
    </div>
  );
}

function ActiveRentalsCard({
  data,
  isLoading,
}: {
  data?: StatsT["activeRentals"];
  isLoading: boolean;
}) {
  const count = data?.count ?? 0;
  const longest = data?.longestOpenMin ?? 0;
  const isStuck = longest >= 60 * 6; // 6h+ open suggests a stuck rental
  return (
    <div className="card overflow-hidden p-5">
      <div className="absolute left-0 top-0 h-full w-0.5 bg-gradient-to-b from-brand-orange/80 to-brand-orangeDark/40" />
      <div className="stat-label">Active rentals</div>
      <div
        className={`mt-2 stat-num ${
          isStuck ? "text-orange-300" : "text-orange-100"
        }`}
      >
        {isLoading ? "—" : count.toLocaleString()}
      </div>
      <div className="mt-1 text-xs text-zinc-500">
        {count === 0
          ? "no batteries out"
          : `longest open: ${fmtDuration(longest)}`}
      </div>
    </div>
  );
}

export function Stats() {
  const [window, setWindow] = useState<StatsWindow>("24h");
  const { data, isLoading } = useStats(window);

  const cabTotal = data?.cabinets.total ?? 0;
  const cabOnline = data?.cabinets.online ?? 0;
  const batTotal = data?.batteries.total ?? 0;
  const batFaulty = data?.batteries.faulty ?? 0;

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      <StatCard
        label="Cabinets online"
        value={isLoading ? "—" : `${cabOnline} / ${cabTotal}`}
        sub={cabTotal === 0 ? "no data yet" : "live"}
        accent="purple"
      />
      <StatCard
        label="Batteries OK"
        value={isLoading ? "—" : `${batTotal - batFaulty} / ${batTotal}`}
        sub={batFaulty > 0 ? `${batFaulty} flagged` : "all healthy"}
        accent="orange"
      />
      <EventsCard
        data={data?.events}
        isLoading={isLoading}
        window={window}
        onWindowChange={setWindow}
      />
      <ActiveRentalsCard
        data={data?.activeRentals}
        isLoading={isLoading}
      />
    </div>
  );
}
